import { Button, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useEffect, useRef, useState } from 'react';
import { Page } from '../../components/Page';
import ProgressIndicator from './ProgressIndicator';
import Step1TourDetails from './Step1TourDetails';
import Step2GuestDetails from './Step2GuestDetails';
import Step4BillingDetails from './Step4BillingDetails';
import Step3OptionalExtras from './Step3OptionalExtras';
import Step5Summary from './Step5Summary';
import LeftIcon from '../../icons/RightArrow';
import clsx from 'clsx';
import { RouteComponentProps } from 'react-router-dom';
import useReactRouter from 'use-react-router';
import useRouter from 'use-react-router';
import { useSnackbar } from 'notistack';
import { LoadingOverlay } from '../../components/LoadingOverlay/LoadingOverlay';
import getGroupByRef from './getGroupByRef.api';
import { Group, GroupBooking } from '../../graphql/groups.datatypes';
import { getHost } from '../../graphql/GraphQlProvider';
import {
    BookingData,
    convertGroupBookingToBookingData,
    getRoomsDataOptions,
    GuestData,
    newBookingData,
} from './helperFunctions';
import submitBookingTool, { SubmitBookingToolInput, } from './submitBookingTool.api';
import moment from 'moment';
import { T, useT } from 'react-polyglot-hooks';
import ContactUs from '../../components/ContactUs';
import getGroupBooking from '../GroupBookings/getGroupBooking.api';
import anyLoading from '../../utils/anyLoading';
import getMyGroupBooking from '../GroupBookings/getMyGroupBooking.api';
import Step0Intro from './Step0Intro';
import { formatGraphQlDate, formatMoney } from '../../utils/formatters';
import InfoModal from './InfoModal';
import PaymentCheckoutFrame from '../Payments/PaymentCheckoutFrame';
import { BillingParty } from '../../graphql/billing_party.datatypes';
import ThankYou from './ThankYou';
import ErrorPage from './ErrorPage';

const stepBgImage = require('../../../assets/img/dot-bg.svg');

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(0),
        backgroundColor: theme.palette.white,
    },
    scrollContent: {
        height: 'calc(100vh - 84px)',
        overflowX: 'hidden',
        position: 'relative'
    },
    container: {
        minHeight: '100%',

        [theme.breakpoints.down('xs')]: {
            paddingBottom: theme.spacing(12),
        },
        [theme.breakpoints.up('sm')]: {
            paddingBottom: theme.spacing(8),
        },
    },
    containerBg: {
        backgroundColor: theme.palette.background.default,
        backgroundImage: `url(${stepBgImage})`,
        backgroundRepeat: 'repeat-x',
        backgroundPositionY: 'bottom',
        backgroundAttachment: 'scroll',
        backgroundSize: 350,

        [theme.breakpoints.down('xs')]: {
            backgroundImage: 'none'
        }
    },
    content: {
        maxWidth: 960,
        paddingTop: theme.spacing(3),

        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
        [theme.breakpoints.up('md')]: {
            width: '80%',
            margin: 'auto',
        },
    },
    buttonContainer: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(7),
        display: 'flex',
        flexDirection: 'row',
        maxWidth: 960,

        [theme.breakpoints.down('sm')]: {
            width: '100%',
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3)
        },
        [theme.breakpoints.up('md')]: {
            width: '80%',
            margin: 'auto',
        },
    },
    bookNowContainer: {
        width: '100%',
        textAlign: 'center',
        paddingBottom: theme.spacing(3),
    },
    button: {
        borderRadius: 0,
        borderWidth: 2,
        textTransform: 'none',

        '&:hover, &.Mui-focusVisible': {
            borderRadius: 0,
            borderWidth: 2,
            textTransform: 'none',
        },

        '&.Mui-disabled': {
            color: theme.palette.white,
            opacity: 1,
            backgroundColor: '#696969'
        },

        '&.MuiButton-outlined': {
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main
        },
    },
    disabledCta: {
        '&.MuiButton-outlined.Mui-disabled': {
            backgroundColor: 'transparent',
            color: '#696969',
            borderColor: '#696969',
            borderWidth: 2,
        },
    },
    buttonStepOne: {
        marginRight: -theme.spacing(2.5),

        [theme.breakpoints.down('sm')]: {
            marginRight: theme.spacing(1.5)
        }
    },
    bookNow: {
        fontSize: theme.typography.h1.fontSize,
    },
    backButton: {
        textTransform: 'none',
        '&:hover, &.Mui-focusVisible': {
            textTransform: 'none',
        },
    },
    labelIcon: {
        width: 20,
        marginRight: theme.spacing(1)
    },
}));

export interface CtaSettingsType {
    label: string;
    handler: () => void;
    disabled?: boolean;
}

export const BookingToolContent = (props: {
    group: Group;
    adminVersion?: boolean;
    booking?: GroupBooking | void;
}) => {
    const {booking, adminVersion} = props;
    const isExistingBooking = !!booking;
    const {history} = useRouter();
    const classes = useStyles();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [activeStep, setActiveStep] = useState(adminVersion ? 2 : 0);
    const [group, setGroup] = useState({...props.group});
    const [bookingData, setBookingData] = useState<BookingData>(
        newBookingData(group)
    );

    const [allGuestData, setAllGuestData] = useState<GuestData[]>([
        ...bookingData.roomsData[0].guestsData,
    ]);
    const steps = [
        'Intro',
        'Your Booking',
        'Travelers',
        'Extras',
        'Billing Details',
        'Payment Summary',
    ];
    const {enqueueSnackbar} = useSnackbar();
    const [sendSubmitBookingTool, submitBookingToolData] = submitBookingTool();
    const t = useT();

    const currentTZ = moment.tz.guess();
    const currentDate = moment(new Date()).tz(currentTZ).utc()

    const afterCutoff = !adminVersion && moment(group.admissions_cutoff).add(1, 'day').isSameOrBefore(
        currentDate
    );
    const afterBookingEditCutoff = !adminVersion && moment(group.booking_tool_edit_cutoff).add(1, 'day').isSameOrBefore(
        currentDate
    );
    const [customBooking, setCustomBooking] = useState(false);

    const [newBillingParty, setNewBillingParty] = useState<BillingParty | undefined>();
    const [bookingSubmitted, setBookingSubmitted] = useState(false);

    useEffect(() => {
        //check if booking allowed?
        //only guests of same trip length
        if (!bookingSubmitted) {
            if (booking && booking.admin_locked) {
                setCustomBooking(true);
            }

            setBookingData(
                booking
                    ? convertGroupBookingToBookingData(props.group, booking, setGroup)
                    : newBookingData(props.group)
            );

            setNewBillingParty(undefined);

            if (adminVersion && activeStep < 2) setActiveStep(2);
        }
    }, [booking, props.group]);

    useEffect(() => {
        setAllGuestData(
            bookingData.roomsData.reduce(
                (accumulator: GuestData[], roomData) =>
                    accumulator.concat(roomData.guestsData),
                []
            )
        );
    }, [bookingData.roomsData]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        if (isExistingBooking) {
            if (activeStep === 0) setActiveStep(1);
        }
        if (isExistingBooking && !adminVersion) {
            if (activeStep === 4) setActiveStep(5);
        }
        if (activeStep === 3) {
            const roomsData = getRoomsDataOptions(
                group,
                bookingData,
                bookingData.roomsData
            );
            setBookingData({...bookingData, roomsData});
        }
        if (adminVersion && activeStep < 2) setActiveStep(2);
    }, [booking, activeStep]);

    const handleSubmitBooking = (signature?: string) => {
        setBookingSubmitted(true);
        //sanitise / translate to graphql objects
        const sanitisedData: SubmitBookingToolInput = {
            data: {
                booking_id: booking ? booking.id : undefined,
                group_id: group.id,
                start_date: formatGraphQlDate(bookingData.nights.start)!,//bookingData.nights.start.format('YYYY-MM-DD'),
                end_date: formatGraphQlDate(bookingData.nights.end)!,//bookingData.nights.end.format('YYYY-MM-DD'),
                billingPartiesData: bookingData.billingPartiesData.map(
                    (billingPartyData) => ({
                        ...billingPartyData,
                        onlyParty: undefined,
                        isValid: undefined,
                        signature: signature ? signature : undefined,
                    })
                ),
                roomsData: bookingData.roomsData.map((roomData) => ({
                    ...roomData,
                    isValid: undefined,
                    hotelName: undefined,
                    guestsData: roomData.guestsData.map((guestData) => ({
                        ...guestData,
                        isValid: undefined,
                        roomId: undefined,
                        dob: formatGraphQlDate(guestData.dob)!,//moment(new Date(guestData.dob)).format('YYYY-MM-DD'),
                        optionValues: guestData.optionValues
                            .filter((optionValue) => optionValue.value > 0)
                            .map((optionValue) => ({
                                ...optionValue,
                                groupResourceOption: undefined,
                                adminEdited: undefined,
                                groupResourceOptionId: optionValue.groupResourceOption.id,
                            })),
                    })),
                    optionValues: roomData.optionValues
                        .filter((optionValue) => optionValue.value > 0)
                        .map((optionValue) => ({
                            ...optionValue,
                            groupResourceOption: undefined,
                            adminEdited: undefined,
                            groupResourceOptionId: optionValue.groupResourceOption.id,
                        })),
                })),
            },
        };

        //send to server.
        sendSubmitBookingTool({variables: sanitisedData})
            .then((response) => {
                const newBooking = response.data!.submitBookingTool;
                if (adminVersion) {
                    history.push('/groups/' + newBooking.group_id);
                } else {
                    if (isExistingBooking) {
                        history.goBack();
                    } else {
                        //Make Payment for first booking party
                        const firstBillingParty = newBooking.billingParties && newBooking.billingParties[0];
                        if (firstBillingParty) {

                            //Fix for credit card fee in payment window.
                            //This should be captured in the graph ql request. But that leads to dangerous and slow query. So this fix is an easier solution.
                            firstBillingParty.booking = {
                                id: newBooking.id,
                                group_id: group.id,
                                group: {...group},
                                soft_cancelled: false,
                                disabled: false,
                            };

                            setNewBillingParty(firstBillingParty);
                            if (group.default_authorize_members) {
                                setOpenPaymentModal(true);
                            }
                        } else {
                            enqueueSnackbar('New billing party not found. Deposit payment must be made later.', {variant: 'error'});
                            onClientBookingFinished();
                        }
                    }
                }
            })
            .catch(() => {
                setShowErrorPage(true);
                enqueueSnackbar('Booking Submission Failed!', {variant: 'error'});
            });
    };


    const [showThankYou, setShowThankYou] = useState(false);
    const [showErrorPage, setShowErrorPage] = useState(false);

    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const containerClassNames = clsx(classes.container, activeStep !== 0 && classes.containerBg)
    const [ctaSettings, setCtaSettings] = useState<CtaSettingsType | null>(null);

    const [openSignModal, setSignModal] = React.useState(false);
    const handleSignModalOpen = () => {
        if (!submitBookingToolData.loading) {
            setSignModal(true);
        }
    };
    const handleSignModalClose = () => setSignModal(false);
    const onSigned = (signature: string) => {
        handleSubmitBooking(signature);
    };

    const [openPaymentCheckModal, setPaymentCheckModal] = React.useState(false);
    const [contactUsModal, setContactUsModal] = React.useState(false);
    const [openPaymentModal, setOpenPaymentModal] = React.useState(false);

    const onClientBookingFinished = () => {
        //close all modals and show thank you.
        setShowThankYou(true);
        setOpenPaymentModal(false);
        setSignModal(false);
        setPaymentCheckModal(false);
        setContactUsModal(false);
        setOpenPaymentModal(false);
        enqueueSnackbar('Booking Submitted', {variant: 'success'});
    };

    const eachBillingPartyHasAtLeastOneGuest = () => {
        if (activeStep !== 4) {
            return true;
        }
        if (adminVersion) {
            return true;
        }

        const map = new Map();
        bookingData.billingPartiesData.forEach((billingPartyData) => {
            map.set(billingPartyData.id, false);
        });
        allGuestData.forEach((guestData) => {
            map.set(guestData.billingPartyId, true);
        });

        let result = true;
        map.forEach((value) => {
            if (!value) {
                result = false;
            }
        });

        return result;
    }

    return (
        <React.Fragment>
            {!isExistingBooking && afterCutoff && (
                <LoadingOverlay loading={submitBookingToolData.loading}>
                    <div className={classes.scrollContent} ref={scrollRef}>
                        <div className={containerClassNames}>
                            <div className={classes.content}>
                                <Typography variant="body1">
                                    <T phrase="cutoff-date"/>
                                </Typography>
                                <br/>
                                <br/>
                                <Button variant="outlined" onClick={handleClickOpen}>
                                    <T phrase="Contact us"/>
                                </Button>

                                <ContactUs
                                    onClose={handleClose}
                                    open={open}
                                    groupId={group.id}
                                    heading={'Contact us'}
                                    message={String(t('cutoff-date'))}
                                    adminMessage={`Admissions cut off for ${group.id} `}
                                />
                            </div>
                        </div>
                    </div>
                </LoadingOverlay>
            )}
            {isExistingBooking && afterBookingEditCutoff && (
                <LoadingOverlay loading={submitBookingToolData.loading}>
                    <div className={classes.scrollContent} ref={scrollRef}>
                        <div className={containerClassNames}>
                            <div className={classes.content}>
                                <Typography variant="body1">
                                    <T phrase="can-edit-cutoff-date"/>
                                </Typography>
                                <br/>
                                <br/>
                                <Button variant="outlined" onClick={handleClickOpen}>
                                    <T phrase="Contact us"/>
                                </Button>

                                <ContactUs
                                    onClose={handleClose}
                                    open={open}
                                    groupId={group.id}
                                    heading={'Contact us'}
                                    message={String(t('can-edit-cutoff-date'))}
                                    adminMessage={`Booking edit cut off for ${group.id} `}
                                />
                            </div>
                        </div>
                    </div>
                </LoadingOverlay>
            )}

            {customBooking && (!afterCutoff || !afterBookingEditCutoff) && (
                <div
                    className={classes.scrollContent}
                    ref={scrollRef}
                >
                    <div className={containerClassNames}>
                        <div className={classes.content}>
                            <Typography variant="h1">
                                <T phrase="Custom Booking."/>
                            </Typography>
                            <br/>
                            <br/>
                            <Typography variant="body1">
                                <T phrase="custom-booking"/>
                            </Typography>
                            <br/>
                            <br/>
                            <Button variant="outlined" onClick={handleClickOpen}>
                                <T phrase="Contact us"/>
                            </Button>

                            <ContactUs
                                onClose={handleClose}
                                open={open}
                                groupId={group.id}
                                heading={'Contact us'}
                                message={String(t('custom-booking'))}
                                adminMessage={`Custom booking for ${booking &&
                                booking.id} update request.`}
                            />
                        </div>
                    </div>
                </div>
            )}

            {((!isExistingBooking) || (isExistingBooking && !afterBookingEditCutoff)) && !customBooking && (
                <LoadingOverlay loading={submitBookingToolData.loading}>
                    <div
                        className={classes.scrollContent}
                        ref={scrollRef}
                    >
                        <div className={containerClassNames}>
                            {!newBillingParty && !adminVersion && activeStep > 0 && (
                                <div className={classes.content}>
                                    <ProgressIndicator
                                        logo={getHost() + group.tour!.image_logo_url!}
                                        setActiveStep={setActiveStep}
                                        activeStep={activeStep}
                                        isExistingBooking={isExistingBooking}
                                        adminVersion={!!adminVersion}
                                        steps={steps}
                                        activeStepValid={bookingData.validData[activeStep]}
                                    />
                                </div>
                            )}
                            {!showThankYou && !showErrorPage && (
                                <div className={classes.content}>
                                    {activeStep === 0 && (
                                        <Step0Intro setActiveStep={setActiveStep} group={group}/>
                                    )}
                                    {activeStep === 1 && (
                                        <Step1TourDetails
                                            setActiveStep={setActiveStep}
                                            group={group}
                                        />
                                    )}
                                    {activeStep === 2 && (
                                        <Step2GuestDetails
                                            setActiveStep={setActiveStep}
                                            group={group}
                                            bookingData={bookingData}
                                            setBookingData={setBookingData}
                                            setCtaSettings={setCtaSettings}
                                            isExistingBooking={isExistingBooking}
                                            readonly={isExistingBooking && !adminVersion}
                                        />
                                    )}
                                    {activeStep === 3 && (
                                        <Step3OptionalExtras
                                            setActiveStep={setActiveStep}
                                            group={group}
                                            bookingData={bookingData}
                                            setGroup={setGroup}
                                            setBookingData={setBookingData}
                                            allGuestData={allGuestData}
                                            adminVersion={adminVersion}
                                        />
                                    )}
                                    {activeStep === 4 && (
                                        <Step4BillingDetails
                                            bookingData={bookingData}
                                            setBookingData={setBookingData}
                                            allGuestData={allGuestData}
                                            setCtaSettings={setCtaSettings}
                                            billingPartyEmpty={eachBillingPartyHasAtLeastOneGuest()}
                                        />
                                    )}
                                    {activeStep === 5 && (
                                        <Step5Summary
                                            setActiveStep={setActiveStep}
                                            group={group}
                                            bookingData={bookingData}
                                            setBookingData={setBookingData}
                                            adminVersion={adminVersion}
                                            allGuestData={allGuestData}
                                        />
                                    )}
                                </div>
                            )}

                            {!showThankYou && !showErrorPage && activeStep < steps.length && activeStep > 0 && (
                                <div
                                    className={classes.buttonContainer}
                                    style={{justifyContent: ctaSettings ? 'space-between' : 'flex-end'}}
                                >
                                    {ctaSettings && (
                                        <Button
                                            size="large"
                                            color="primary"
                                            variant="contained"
                                            className={clsx(classes.button, ctaSettings.disabled && classes.disabledCta)}
                                            onClick={ctaSettings.handler}
                                            disabled={ctaSettings.disabled === true}
                                        >
                                            {ctaSettings.label}
                                        </Button>
                                    )}

                                    <Button
                                        onClick={() => {
                                            if (activeStep < steps.length - 1)
                                                setActiveStep(activeStep + 1);
                                            if (activeStep === steps.length - 1)
                                                isExistingBooking || adminVersion ? handleSubmitBooking('admin signed') : handleSignModalOpen();
                                        }}
                                        size="large"
                                        color="primary"
                                        variant="contained"
                                        className={clsx(classes.button, {
                                            [classes.buttonStepOne]: activeStep === 1
                                        })}
                                        disabled={!bookingData.validData[activeStep] || !eachBillingPartyHasAtLeastOneGuest()}
                                    >
                                        <LeftIcon color="inherit" className={classes.labelIcon}/>
                                        {activeStep === steps.length - 1 ? 'Accept and Continue' : 'Next'}
                                    </Button>
                                </div>
                            )}

                            {showThankYou && (
                                <div className={classes.content}>
                                    <ThankYou group={group}/>
                                </div>
                            )}

                            {showErrorPage && (
                                <div className={classes.content}>
                                    <ErrorPage/>
                                </div>
                            )}

                            {openSignModal && (
                                <InfoModal onClose={handleSignModalClose} open={true} title="Sign"
                                           body="Please type your full name to acknowledge that you agree to all Terms & Conditions, Payment Schedule, Cancellation Policy, and Optional Travel Insurance Information."
                                           onSigned={onSigned}/>
                            )}

                            {openPaymentCheckModal && !contactUsModal && (
                                <InfoModal open={true} title="Payment"
                                           body="We noticed that you opted out of making your payment online. Please contact us for mailing information to send a bank check, money order, or personal check. Payment must be received within 7 days to confirm booking."
                                           action={[
                                               <Button key={1} onClick={() => setPaymentCheckModal(false)}
                                                       variant="contained">
                                                   <T phrase="Make a Payment"/>
                                               </Button>,
                                               <Button key={2} onClick={() => setContactUsModal(true)}
                                                       variant="contained">
                                                   <T phrase="Contact Us"/>
                                               </Button>,
                                           ]}/>
                            )}

                            {contactUsModal && (
                                <ContactUs
                                    onClose={() => setContactUsModal(false)}
                                    onSendClick={onClientBookingFinished}
                                    open={true}
                                    groupId={group.id}
                                    heading={'Contact us'}
                                    message="Please let us know how we can help."
                                    adminMessage={'Billing party ' + newBillingParty!.id + ' postal payment request.'}
                                />
                            )}
                        </div>
                    </div>
                </LoadingOverlay>
            )}

            {openPaymentModal && newBillingParty && (
                <PaymentCheckoutFrame
                    open={true}
                    billingParty={newBillingParty}
                    billingPartyMemberId={newBillingParty.primaryPayingMember!.id}
                    isFirstPayment={true}
                    amount={newBillingParty.overduePayments!}
                    message={'Thank you for completing your booking. Please pay the initial deposit of ' + formatMoney(newBillingParty.overduePayments!)}
                    onClose={() => setPaymentCheckModal(true)}
                    onSuccess={onClientBookingFinished}
                />
            )}
        </React.Fragment>
    );
};

const BookingTool = () => {
    const {
        history,
        match,
        location,
    }: RouteComponentProps<{ id: string; booking_id: string }> = useReactRouter();
    const {id, booking_id} = match.params;
    const bookingId = booking_id ? Number(booking_id) : -1;
    const isAdmin = location.pathname.indexOf('/admin_booking/') === 0;
    const classes = useStyles();
    const groupData = getGroupByRef(id);
    const bookingData = isAdmin
        ? getGroupBooking(bookingId, false)
        : getMyGroupBooking(bookingId, false);
    const loadingData = anyLoading(groupData, bookingData);
    const {enqueueSnackbar} = useSnackbar();
    const booking = isAdmin
        ? bookingData.data && bookingData.data.groupBooking
        : bookingData.data && bookingData.data.myGroupBooking;

    useEffect(() => {
        if (groupData.error) {
            enqueueSnackbar(
                `There was an error loading the reference ${id}. Please contact admin@wcv.com`,
                {variant: 'error'}
            );
            history.push('/');
        }
    }, [groupData.error]);

    return (
        <Page className={classes.root}>
            <LoadingOverlay loading={loadingData.loading}>
                {groupData.data && groupData.data.groupByRef && (
                    <BookingToolContent
                        adminVersion={isAdmin}
                        group={groupData.data.groupByRef}
                        booking={booking}
                    />
                )}
            </LoadingOverlay>
        </Page>
    );
};

export default BookingTool;
